import { Component, Input } from '@angular/core';
import { DisplayOptions, Filter, IdentifiedItemConfig } from '../../models/filter-models';
import { AbyssJewelsCategory, AttackWeaponsCategory, CasterWeaponsCategory, DefenceType, EnergyShieldArmourCategory, GemWeaponsCategory, JewelleryCategory, JewelsCategory, LifeArmourCategory, ModItemCategoryType, QuiversCategory, TimeLostJewelsCategory } from "../../models/enums";
import { DisplayType } from "../../models/enums";
import { Rarity } from "../../models/enums";
import { ArmourType, WeaponType } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { FilterService } from '../../services/filter.service';
import { UniquesTierService } from '../../services/tiering/uniques-tier.service';
import { ChanceBasesTierService } from '../../services/tiering/chance-bases-tier.service';
import { ItemDataService } from '../../services/item-data.service';
import { BaseComponent } from '../../common/base.component';
import { ITEM_CATEGORIES_POE1_ONLY } from '../../models/enums';
import { ActiveRules, ActiveSpecialRules } from '../../services/filter-generator.service';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { FilterTitleComponent } from '../../common/filter-title/filter-title.component';
import { SelectGearTierComponent } from '../../common/select/select-gear-tier/select-gear-tier.component';
import { EquipmentTierPickerComponent } from '../../common/equipment-tier-picker/equipment-tier-picker.component';
import { EquipmentType } from "../../models/equipment-type.enum";
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { FilterCheckboxGroupComponent } from '../../common/filter-checkbox-group/filter-checkbox-group.component';
import { ModTierExplainerComponent } from '../../common/mod-tier-explainer/mod-tier-explainer.component';
import { IdentifiedItemConfigurationComponent } from './identified-item-configuration/identified-item-configuration.component';
import { NavigationService } from '../../services/navigation.service';

@Component({
  selector: 'app-identified-item-filters',
  imports: [FormsModule, AutocompleteComponent, FilterTitleComponent, EquipmentTierPickerComponent,
    SelectGearTierComponent, DynamicContentComponent, FilterCheckboxGroupComponent, ModTierExplainerComponent, IdentifiedItemConfigurationComponent],
  templateUrl: './identified-item-filters.component.html',
  styleUrl: './identified-item-filters.component.scss'
})
export class IdentifiedItemFiltersComponent extends BaseComponent {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) activeRules!: ActiveRules;
  @Input({ required: true }) activeSpecialRules!: ActiveSpecialRules;

  armourTypes: { name: ArmourType, image: string }[] = [];
  defenceTypes = [
    { name: 'Arm', image: '/img/shields/shield-red.png' },
    { name: 'Eva', image: '/img/shields/shield-green.png' },
    { name: 'ES', image: '/img/shields/shield-blue.png' },
    { name: 'Arm/Eva', image: '/img/shields/shield-red-green.png' },
    { name: 'Arm/ES', image: '/img/shields/shield-red-blue.png' },
    { name: 'Eva/ES', image: '/img/shields/shield-green-blue.png' }
  ];

  identifiedAttackWeaponClasses: { name: WeaponType, image: string }[] = [];

  // Generic helper function to get enum values as category objects
  private getEnumCategories<T extends Record<string, string>>(enumObj: T, excludeValue?: string) {
    return Object.values(enumObj)
      .filter(value => !excludeValue || value !== excludeValue)
      .map(value => ({ name: value }));
  }

  // Category properties
  identifiedAttackWeaponCategories: { name: string }[] = [];
  identifiedCasterWeaponCategories: { name: string }[] = [];
  identifiedGemWeaponCategories: { name: string }[] = [];
  identifiedQuiversCategories: { name: string }[] = [];
  identifiedLifeArmourCategories: { name: string }[] = [];
  identifiedEnergyShieldArmourCategories: { name: string }[] = [];
  identifiedJewelleryCategories: { name: string }[] = [];
  identifiedJewelCategories: { name: string }[] = [];
  identifiedAbyssJewelCategories: { name: string }[] = [];
  identifiedTimeLostJewelCategories: { name: string }[] = [];

  showIdentifiedGearFilters = false;
  showIdentifiedJewelleryFilters = false;
  showIdentifiedJewelsFilters = false;
  showRestrictedModIdentifiedItemsFilters = false;
  showConfiguration = false;

  Rarity = Rarity;
  DisplayType = DisplayType;
  EquipmentType = EquipmentType;

  constructor(
    private filterService: FilterService,
    private uniquesTierService: UniquesTierService,
    private chanceBasesTierService: ChanceBasesTierService,
    private itemDataService: ItemDataService,
    private navigationService: NavigationService
  ) {
    super();

    this.armourTypes = [
      { name: ArmourType.BodyArmours, image: this.itemDataService.getItemData().find(i => i.itemClass === ArmourType.BodyArmours)!.baseTypes[1].image! },
      { name: ArmourType.Helmets, image: this.itemDataService.getItemData().find(i => i.itemClass === ArmourType.Helmets)!.baseTypes[6].image! },
      { name: ArmourType.Gloves, image: this.itemDataService.getItemData().find(i => i.itemClass === ArmourType.Gloves)!.baseTypes[1].image! },
      { name: ArmourType.Boots, image: this.itemDataService.getItemData().find(i => i.itemClass === ArmourType.Boots)!.baseTypes[1].image! },
      { name: ArmourType.Shields, image: this.itemDataService.getItemData().find(i => i.itemClass === ArmourType.Shields)!.baseTypes[0].image! },
    ];

    this.identifiedAttackWeaponClasses = this.itemDataService.getAttackWeaponTypes()
      .map(w => ({ name: w as WeaponType, image: this.itemDataService.getItemData().find(i => i.itemClass === w)!.baseTypes[0].image! }));

    this.identifiedAttackWeaponCategories = this.getEnumCategories(AttackWeaponsCategory, AttackWeaponsCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedCasterWeaponCategories = this.getEnumCategories(CasterWeaponsCategory, CasterWeaponsCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedGemWeaponCategories = this.getEnumCategories(GemWeaponsCategory, GemWeaponsCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedQuiversCategories = this.getEnumCategories(QuiversCategory, QuiversCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedLifeArmourCategories = this.getEnumCategories(LifeArmourCategory, LifeArmourCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedEnergyShieldArmourCategories = this.getEnumCategories(EnergyShieldArmourCategory, EnergyShieldArmourCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedJewelleryCategories = this.getEnumCategories(JewelleryCategory, JewelleryCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedJewelCategories = this.getEnumCategories(JewelsCategory, JewelsCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedAbyssJewelCategories = this.getEnumCategories(AbyssJewelsCategory, AbyssJewelsCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
    this.identifiedTimeLostJewelCategories = this.getEnumCategories(TimeLostJewelsCategory, TimeLostJewelsCategory.All).filter(c => this.poe1 || !ITEM_CATEGORIES_POE1_ONLY.includes(c.name as ModItemCategoryType));
  }

  toggleHighlight(displayOptions: DisplayOptions) {
    displayOptions.active = !displayOptions.active;
  }

  toggleShowAllIdentifiedItems = () => {
    this.filter.showAllIdentifiedItems = !this.filter.showAllIdentifiedItems;
    if (this.filter.showAllIdentifiedItems) {
      this.filter.showIdentifiedGear = false;
      this.filter.showIdentifiedJewellery = false;
      this.filter.showIdentifiedJewels = false;
      this.filter.showIdentifiedCustomItems = false;
      this.filter.hideRemainingIdentifiedItems = false;
      this.filter.hideRemainingIdentifiedItemsIncludingCorrupted = false;
      this.showIdentifiedGearFilters = false;
      this.showIdentifiedJewelleryFilters = false;
      this.showIdentifiedJewelsFilters = false;
    } else {
      this.filter.showIdentifiedGear = true;
      this.filter.showIdentifiedJewellery = true;
      this.filter.showIdentifiedJewels = true;
      this.filter.showIdentifiedCustomItems = true;
      this.filter.hideRemainingIdentifiedItems = true;
      this.filter.hideRemainingIdentifiedItemsIncludingCorrupted = false;
    }
    this.updateFilter();
  }

  toggleHighlightIdentifiedGear = () => {
    this.filter.showIdentifiedGear = !this.filter.showIdentifiedGear;
    if (!this.filter.showIdentifiedGear) {
      this.showIdentifiedGearFilters = false;
    }
    this.updateFilter();
  }

  highlightIdentifiedAttackWeaponCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedAttackWeaponCategories = newValues as AttackWeaponsCategory[];
    this.updateFilter();
  }

  highlightIdentifiedAttackWeaponTypesChanged(newValues: string[]) {
    this.filter.showIdentifiedAttackWeaponTypes = newValues as WeaponType[];
    this.updateFilter();
  }

  highlightIdentifiedCasterWeaponCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedCasterWeaponCategories = newValues as CasterWeaponsCategory[];
    this.updateFilter();
  }

  highlightIdentifiedGemWeaponCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedGemWeaponCategories = newValues as GemWeaponsCategory[];
    this.updateFilter();
  }

  highlightIdentifiedQuiverCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedQuiverCategories = newValues as QuiversCategory[];
    this.updateFilter();
  }

  highlightIdentifiedLifeArmourCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedLifeArmourCategories = newValues as LifeArmourCategory[];
    this.updateFilter();
  }

  highlightIdentifiedLifeArmourTypesDefenceTypesChanged(newValues: string[]) {
    this.filter.showIdentifiedLifeArmourTypesDefenceTypes = newValues as DefenceType[];
    this.updateFilter();
  }

  highlightIdentifiedEnergyShieldArmourCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedEnergyShieldArmourCategories = newValues as EnergyShieldArmourCategory[];
    this.updateFilter();
  }

  highlightIdentifiedEnergyShieldArmourTypesDefenceTypesChanged(newValues: string[]) {
    this.filter.showIdentifiedEnergyShieldArmourTypesDefenceTypes = newValues as DefenceType[];
    this.updateFilter();
  }

  toggleHighlightIdentifiedJewellery = () => {
    this.filter.showIdentifiedJewellery = !this.filter.showIdentifiedJewellery;
    if (!this.filter.showIdentifiedJewellery) {
      this.showIdentifiedJewelleryFilters = false;
    }
    this.updateFilter();
  }

  highlightIdentifiedJewelleryCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedJewelleryCategories = newValues as JewelleryCategory[];
    this.updateFilter();
  }

  toggleHighlightIdentifiedJewels = () => {
    this.filter.showIdentifiedJewels = !this.filter.showIdentifiedJewels;
    if (!this.filter.showIdentifiedJewels) {
      this.showIdentifiedJewelsFilters = false;
    }
    this.updateFilter();
  }

  highlightIdentifiedJewelCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedJewelCategories = newValues as JewelsCategory[];
    this.updateFilter();
  }

  highlightIdentifiedAbyssJewelCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedAbyssJewelCategories = newValues as AbyssJewelsCategory[];
    this.updateFilter();
  }

  highlightIdentifiedTimeLostJewelCategoriesChanged(newValues: string[]) {
    this.filter.showIdentifiedTimeLostJewelCategories = newValues as TimeLostJewelsCategory[];
    this.updateFilter();
  }

  toggleHighlightIdentifiedCustomItems = () => {
    this.filter.showIdentifiedCustomItems = !this.filter.showIdentifiedCustomItems;
    this.updateFilter();
  }

  toggleHighlightRestrictedModIdentifiedItems = () => {
    this.filter.showRestrictedModIdentifiedItems = !this.filter.showRestrictedModIdentifiedItems;
    if (!this.filter.showRestrictedModIdentifiedItems) {
      this.filter.showRestrictedModIdentifiedItemsDelve = false;
      this.filter.showRestrictedModIdentifiedItemsIncursion = false;
      this.filter.showRestrictedModIdentifiedItemsBreach = false;
      this.showRestrictedModIdentifiedItemsFilters = false;
    } else {
      this.filter.showRestrictedModIdentifiedItemsDelve = true;
      this.filter.showRestrictedModIdentifiedItemsIncursion = true;
      this.filter.showRestrictedModIdentifiedItemsBreach = true;
    }
    this.updateFilter();
  }

  toggleHighlightRestrictedModIdentifiedItemsDelve = () => {
    this.filter.showRestrictedModIdentifiedItemsDelve = !this.filter.showRestrictedModIdentifiedItemsDelve;
    if (!this.filter.showRestrictedModIdentifiedItemsDelve && !this.filter.showRestrictedModIdentifiedItemsBreach && !this.filter.showRestrictedModIdentifiedItemsIncursion) {
      this.filter.showRestrictedModIdentifiedItems = false;
      this.showRestrictedModIdentifiedItemsFilters = false;
    }
    this.updateFilter();
  }

  toggleHighlightRestrictedModIdentifiedItemsIncursion = () => {
    this.filter.showRestrictedModIdentifiedItemsIncursion = !this.filter.showRestrictedModIdentifiedItemsIncursion;
    if (!this.filter.showRestrictedModIdentifiedItemsIncursion && !this.filter.showRestrictedModIdentifiedItemsBreach && !this.filter.showRestrictedModIdentifiedItemsDelve) {
      this.filter.showRestrictedModIdentifiedItems = false;
      this.showRestrictedModIdentifiedItemsFilters = false;
    }
    this.updateFilter();
  }

  toggleHighlightRestrictedModIdentifiedItemsBreach = () => {
    this.filter.showRestrictedModIdentifiedItemsBreach = !this.filter.showRestrictedModIdentifiedItemsBreach;
    if (!this.filter.showRestrictedModIdentifiedItemsBreach && !this.filter.showRestrictedModIdentifiedItemsDelve && !this.filter.showRestrictedModIdentifiedItemsIncursion) {
      this.filter.showRestrictedModIdentifiedItems = false;
      this.showRestrictedModIdentifiedItemsFilters = false;
    }
    this.updateFilter();
  }

  highlightRestrictedModIdentifiedItemsWeaponTypesChanged(newValues: string[]) {
    this.filter.showRestrictedModIdentifiedItemsWeaponTypes = newValues as WeaponType[];
    this.updateFilter();
  }

  highlightRestrictedModIdentifiedItemsDefenceTypesChanged(newValues: string[]) {
    this.filter.showRestrictedModIdentifiedItemsDefenceTypes = newValues as DefenceType[];
    this.updateFilter();
  }

  addIdentifiedItemConfiguration() {
    this.filter.identifiedItemConfigs.push(new IdentifiedItemConfig());
    this.updateFilter();
  }

  removeIdentifiedItemConfiguration(index: number) {
    this.filter.identifiedItemConfigs.splice(index, 1);
    this.updateFilter();
  }

  toggleHideRemainingIdentifiedItems = () => {
    this.filter.hideRemainingIdentifiedItems = !this.filter.hideRemainingIdentifiedItems;
    if (!this.filter.hideRemainingIdentifiedItems) {
      this.filter.hideRemainingIdentifiedItemsIncludingCorrupted = false;
    }
    this.updateFilter();
  }

  navigateToShowAllIdentifiedItems() {
    this.navigationService.navigateToFilterTitle('identified-item-show-all');
  }

  updateFilter() {
    this.filterService.updateFilter();
  }
}
